import React, { useState } from 'react';
import { Box, Button, Container, Grid, Typography, TextField, Card, CardContent, Divider, Checkbox, FormControlLabel, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from '../utils/axiosConfig';
import API_URL from '../config';
import { useTranslation } from 'react-i18next';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import PaidIcon from '@mui/icons-material/Paid';
import PersonIcon from '@mui/icons-material/Person';

const DonationCard = styled(Card)(({ theme, selected }) => ({
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(0.5),
  background: selected 
    ? 'linear-gradient(to right, #8B5CF6, #6366F1)'
    : theme.palette.mode === 'dark' ? '#2d2d2d' : '#ffffff',
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  border: selected 
    ? 'none'
    : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(138, 75, 175, 0.3)' : 'rgba(138, 75, 175, 0.2)'}`,
  boxShadow: selected
    ? '0 10px 20px rgba(129, 92, 246, 0.3)'
    : theme.shadows[2],
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 12px 28px ${selected ? 'rgba(129, 92, 246, 0.35)' : 'rgba(129, 92, 246, 0.25)'}`,
  },
  '& .MuiCardContent-root': {
    backgroundColor: selected 
      ? 'transparent'
      : theme.palette.mode === 'dark' ? '#2d2d2d' : '#ffffff',
    borderRadius: '8px',
  }
}));

const AmountTypography = styled(Typography)(({ theme, selected }) => ({
  color: selected ? '#ffffff' : theme.palette.mode === 'dark' ? '#ffffff' : '#333333',
  fontWeight: 700,
  transition: 'all 0.3s ease',
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(to right, #8B5CF6, #6366F1)',
  color: 'white',
  fontWeight: 'bold',
  padding: '12px 32px',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(129, 92, 246, 0.4)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(to right, #7C3AED, #4F46E5)',
    boxShadow: '0 6px 18px rgba(129, 92, 246, 0.5)',
    transform: 'translateY(-2px)',
  },
  '&:focus': {
    outline: 'none',
    boxShadow: `0 0 0 3px ${theme.palette.mode === 'dark' ? 'rgba(167, 139, 250, 0.5)' : 'rgba(196, 181, 253, 0.7)'}`,
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.mode === 'dark' ? 'rgba(138, 75, 175, 0.3)' : 'rgba(138, 75, 175, 0.2)',
      borderRadius: '8px',
    },
    '&:hover fieldset': {
      borderColor: '#6a0dad',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#6a0dad',
    },
  },
}));

const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  borderRadius: '16px',
  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)',
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(138, 75, 175, 0.2)' : 'rgba(138, 75, 175, 0.1)'}`,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  fontWeight: 700,
  color: theme.palette.mode === 'dark' ? '#fff' : '#333',
}));

const DonationPage = () => {
  const { t } = useTranslation();
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [donorNote, setDonorNote] = useState('');

  const donationAmounts = [100, 500, 1000, 10000];

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setIsCustom(false);
  };

  const handleCustomAmountSelect = () => {
    setIsCustom(true);
    setSelectedAmount(parseInt(customAmount) || 0);
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    setCustomAmount(value);
    if (isCustom && value) {
      setSelectedAmount(parseInt(value) || 0);
    }
  };

  const handleDonation = async () => {
    // 驗證金額
    if (selectedAmount <= 0) {
      alert(t('pleaseEnterValidAmount'));
      return;
    }
    
    try {
      setIsProcessing(true);
      
      // 準備捐款資訊，包括捐款者資料
      const donationData = {
        amount: selectedAmount,
        quantity: quantity,
        clientBackUrl: `${window.location.origin}/donation/success`,
        notifyUrl: `${API_URL}/api/donation/payment-notify`,
        // 如果不是匿名，添加捐款者信息
        ...(isAnonymous ? { isAnonymous: true } : {
          donorName: donorName,
          donorEmail: donorEmail,
          donorNote: donorNote
        })
      };
      
      const response = await axios.post(`${API_URL}/api/donation/create-donation-order`, donationData, { withCredentials: true });

      if (response.data.success) {
        // 將綠界回傳的HTML表單插入到頁面中並自動提交
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = 'https://payment.ecpay.com.tw/Cashier/AioCheckOut/V5'; // 正式環境
        // form.action = 'https://payment-stage.ecpay.com.tw/Cashier/AioCheckOut/V5'; // 測試環境(切換測試環境後端也要改）
        form.innerHTML = response.data.htmlRedirectForm;
        document.body.appendChild(form);
        form.submit();
      }
    } catch (error) {
      console.error(t('donationProcessFailed'), error);
      alert(t('donationProcessFailedTryAgain'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 min-h-screen pb-12">
      {/* Hero Banner 區塊 */}
      <div
        className="w-full h-64 bg-cover bg-center flex flex-col items-center justify-center text-white text-3xl font-bold shadow-lg"
        style={{ backgroundImage: "url('/images/myReportsBanner.png')" }}
      >
        <h1 className="text-white uppercase tracking-wide">
          {t('supportAntiScamPlatform')}
        </h1>
        <p className="text-lg font-medium mt-2 text-gray-200">
          {t('donationWillHelp')}
        </p>
      </div>
      
      <Container maxWidth="md" sx={{ mt: 6 }}>
        {/* 快速捐款區塊 */}
        <SectionPaper elevation={3}>
          <SectionTitle variant="h5">
            <FlashOnIcon color="primary" />
            {t('quickDonation') || '快速捐款'}
          </SectionTitle>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('quickDonationDesc') || '選擇一個捐款金額並設定數量，快速完成支持'}
          </Typography>
          
          <Grid container spacing={3} justifyContent="center" mb={4}>
            {donationAmounts.map((amount) => (
              <Grid item xs={6} sm={3} key={amount}>
                <DonationCard
                  selected={selectedAmount === amount && !isCustom}
                  onClick={() => handleAmountSelect(amount)}
                >
                  <CardContent>
                    <AmountTypography 
                      variant="h5" 
                      component="div" 
                      align="center"
                      selected={selectedAmount === amount && !isCustom}
                    >
                      NT$ {amount.toLocaleString()}
                    </AmountTypography>
                  </CardContent>
                </DonationCard>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ maxWidth: 300, mx: 'auto', mb: 4 }}>
            <StyledTextField
              fullWidth
              type="number"
              label={t('quantity')}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1 }}
            />
          </Box>
        </SectionPaper>

        {/* 自定義捐款區塊 */}
        <SectionPaper elevation={3}>
          <SectionTitle variant="h5">
            <PaidIcon color="primary" />
            {t('customDonation') || '自定義捐款'}
          </SectionTitle>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('customDonationDesc') || '輸入您想捐贈的具體金額'}
          </Typography>

          <Grid container spacing={2} alignItems="center" sx={{ maxWidth: 500, mx: 'auto', mb: 4 }}>
            <Grid item xs={8}>
              <StyledTextField
                fullWidth
                type="number"
                label={t('enterAmount') || '輸入金額'}
                value={customAmount}
                onChange={handleCustomAmountChange}
                onFocus={handleCustomAmountSelect}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleCustomAmountSelect}
                sx={{
                  height: '56px',
                  borderColor: isCustom ? '#8B5CF6' : 'rgba(138, 75, 175, 0.3)',
                  color: isCustom ? '#8B5CF6' : 'inherit',
                  '&:hover': {
                    borderColor: '#8B5CF6',
                  }
                }}
              >
                {t('use') || '使用'}
              </Button>
            </Grid>
          </Grid>
        </SectionPaper>

        {/* 捐款者信息區塊 */}
        <SectionPaper elevation={3}>
          <SectionTitle variant="h5">
            <PersonIcon color="primary" />
            {t('donorInformation') || '捐款者資訊'}
          </SectionTitle>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('donorInfoDesc') || '讓我們認識您，我們將發送感謝函到您的信箱（可選）'}
          </Typography>

          <FormControlLabel
            control={
              <Checkbox 
                checked={isAnonymous} 
                onChange={(e) => setIsAnonymous(e.target.checked)} 
              />
            }
            label={t('donateAnonymously') || '匿名捐款'}
            sx={{ mb: 2 }}
          />

          {!isAnonymous && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  label={t('donorName') || '姓名'}
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  disabled={isAnonymous}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  type="email"
                  label={t('donorEmail') || '電子郵件'}
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  disabled={isAnonymous}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  multiline
                  rows={3}
                  label={t('donorNote') || '留言'}
                  value={donorNote}
                  onChange={(e) => setDonorNote(e.target.value)}
                  disabled={isAnonymous}
                  placeholder={t('donorNotePlaceholder') || '請留下您想對我們說的話...'}
                />
              </Grid>
            </Grid>
          )}
        </SectionPaper>

        {/* 總金額與提交按鈕 */}
        <Box 
          sx={{ 
            textAlign: 'center',
            p: 4,
            backgroundColor: 'rgba(138, 75, 175, 0.05)',
            borderRadius: '16px',
            mb: 4
          }}
        >
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(to right, #8B5CF6, #6366F1)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('totalAmount')}: NT$ {(selectedAmount * quantity).toLocaleString()}
          </Typography>
          <GradientButton
            variant="contained"
            size="large"
            onClick={handleDonation}
            disabled={isProcessing || selectedAmount <= 0}
            sx={{ mt: 2 }}
          >
            {isProcessing ? t('processing') : t('donateNow')}
          </GradientButton>
        </Box>
      </Container>
    </Box>
  );
};

export default DonationPage; 